<template>
  <div>
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5>Фильтры</h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-1">
            <label>Поиск по названию</label>
            <b-form-input
                v-model="searchQuery"
                placeholder="Beatles - Yesterday"
                @keyup.enter="fetchTracksList()"
            />
          </b-col>
          <b-col cols="12" md="2" class="mb-1">
            <label>Файл</label>
            <v-select
                v-model="filterHasFile"
                :options="boolOptions"
                :reduce="o => o.value"
                :clearable="true"
                placeholder="Все"
                @input="fetchTracksList()"
            />
          </b-col>
          <b-col cols="12" md="2" class="mb-1">
            <label>Игнор</label>
            <v-select
                v-model="filterIgnore"
                :options="boolOptions"
                :reduce="o => o.value"
                :clearable="true"
                placeholder="Все"
                @input="fetchTracksList()"
            />
          </b-col>
          <b-col cols="12" md="2" class="mb-1">
            <label>Кавер</label>
            <v-select
                v-model="filterIsCover"
                :options="boolOptions"
                :reduce="o => o.value"
                :clearable="true"
                placeholder="Все"
                @input="fetchTracksList()"
            />
          </b-col>
          <b-col cols="12" md="2" class="mb-1">
            <label>Есть каверы</label>
            <v-select
                v-model="filterHasCovers"
                :options="boolOptions"
                :reduce="o => o.value"
                :clearable="true"
                placeholder="Все"
                @input="fetchTracksList()"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6" md="2" class="mb-1">
            <label>Прослушивания от</label>
            <b-form-input
                v-model="filterPlayCountMin"
                type="number"
                placeholder="0"
                @change="fetchTracksList()"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label>Прослушивания до</label>
            <b-form-input
                v-model="filterPlayCountMax"
                type="number"
                placeholder="999999"
                @change="fetchTracksList()"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label>Сложность от</label>
            <b-form-input
                v-model="filterDifficultyMin"
                type="number"
                min="1"
                max="10"
                placeholder="1"
                @change="fetchTracksList()"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label>Сложность до</label>
            <b-form-input
                v-model="filterDifficultyMax"
                type="number"
                min="1"
                max="10"
                placeholder="10"
                @change="fetchTracksList()"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-overlay :show="isLoading" rounded="sm">
        <b-table
            ref="refTracksListTable"
            :items="tracks"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            empty-text="Нет треков"
            class="position-relative"
            sticky-header="75vh"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            no-local-sorting
        >

          <template #cell(artists)="data">
            <span v-for="(artist, index) in data.item.artists" :key="index">
              <b-link
                  v-if="artist.id"
                  :to="{ name: 'artist-view', params: { id: artist.id }}"
                  class="font-weight-bold"
              >
                {{ artist.title }}
              </b-link>
              <span v-else class="font-weight-bold">{{ artist.title }}</span>
              <span v-if="index < data.item.artists.length - 1"> / </span>
            </span>
          </template>

          <template #cell(track)="data">
            <span class="font-weight-bold">{{ data.item.track }}</span>
            <b-badge v-if="data.item.is_cover" variant="light-warning" class="font-small-2 ml-25">Cover</b-badge>
            <b-badge v-if="data.item.has_covers" variant="light-info" class="font-small-2 ml-25">Has covers</b-badge>
          </template>

          <template #cell(file)="data">
            <feather-icon
                :icon="data.item.file ? 'CheckCircleIcon' : 'XCircleIcon'"
                :class="data.item.file ? 'text-success' : 'text-danger'"
                size="18"
            />
          </template>

          <template #cell(play_count)="data">
            <span>{{ data.item.play_count ? data.item.play_count.toLocaleString('ru-RU') : 0 }}</span>
          </template>

          <template #cell(difficulty_rating)="data">
            <b-form-rating
                :value="data.item.difficulty_rating"
                :stars="10"
                inline
                no-border
                size="sm"
                variant="warning"
                class="p-0"
                @change="updateDifficultyRating(data.item, $event)"
            />
          </template>

          <template #cell(ignore)="data">
            <b-badge :variant="data.item.ignore ? 'light-danger' : 'light-success'">
              {{ data.item.ignore ? 'Да' : 'Нет' }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                  :to="{ name: 'track-detail', params: { id: data.item.id } }"
                  variant="primary"
                  class="btn-icon"
                  size="sm"
              >
                <feather-icon icon="ArrowRightCircleIcon"/>
              </b-button>
            </div>
          </template>
        </b-table>

        <b-col cols="12">
          <div class="my-2">
            <b-pagination
                v-if="total > perPage"
                v-model="page"
                :per-page="perPage"
                align="center"
                :total-rows="total"
                first-number
                last-number
                prev-class="prev-item"
                next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </div>
        </b-col>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BLink,
  BBadge,
  BPagination,
  BOverlay,
  BFormRating,
} from 'bootstrap-vue'
import store from '@/store'
import { onUnmounted, ref, watch } from '@vue/composition-api'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import vSelect from 'vue-select'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BButton,
    BLink,
    BBadge,
    BPagination,
    BOverlay,
    BFormRating,
    vSelect,
  },
  setup() {
    const STORE_MODULE_NAME = 'tracks'

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const tracks = ref([])
    const isLoading = ref(false)
    const page = ref(1)
    const perPage = ref(50)
    const total = ref(0)
    const searchQuery = ref('')
    const sortBy = ref('id')
    const isSortDirDesc = ref(true)

    // Filters
    const filterHasFile = ref(null)
    const filterIgnore = ref(null)
    const filterIsCover = ref(null)
    const filterHasCovers = ref(null)
    const filterPlayCountMin = ref(null)
    const filterPlayCountMax = ref(null)
    const filterDifficultyMin = ref(null)
    const filterDifficultyMax = ref(null)

    const boolOptions = [
      { label: 'Да', value: true },
      { label: 'Нет', value: false },
    ]

    const tableColumns = [
      { key: 'id', label: '#', sortable: false },
      { key: 'artists', label: 'Исполнитель', sortable: false },
      { key: 'track', label: 'Название', sortable: true },
      { key: 'file', label: 'Файл', sortable: false },
      { key: 'play_count', label: 'Прослушивания', sortable: true },
      { key: 'difficulty_rating', label: 'Сложность', sortable: true, thClass: 'text-nowrap' },
      { key: 'ignore', label: 'Игнор', sortable: false },
      { key: 'actions', label: 'Действия' },
    ]

    const buildParams = () => {
      const params = {
        page: page.value,
        per_page: perPage.value,
        sort_by: sortBy.value,
        sort_desc: isSortDirDesc.value,
      }
      if (searchQuery.value) params.q = searchQuery.value
      if (filterHasFile.value !== null) params.has_file = filterHasFile.value
      if (filterIgnore.value !== null) params.ignore = filterIgnore.value
      if (filterIsCover.value !== null) params.is_cover = filterIsCover.value
      if (filterHasCovers.value !== null) params.has_covers = filterHasCovers.value
      if (filterPlayCountMin.value) params.play_count_min = filterPlayCountMin.value
      if (filterPlayCountMax.value) params.play_count_max = filterPlayCountMax.value
      if (filterDifficultyMin.value) params.difficulty_rating_min = filterDifficultyMin.value
      if (filterDifficultyMax.value) params.difficulty_rating_max = filterDifficultyMax.value
      return params
    }

    const fetchTracksList = () => {
      isLoading.value = true
      page.value = 1
      store.dispatch('tracks/fetchTracks', buildParams())
        .then(response => {
          tracks.value = response.data.data
          total.value = response.data.meta.total
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const fetchPage = () => {
      isLoading.value = true
      store.dispatch('tracks/fetchTracks', buildParams())
        .then(response => {
          tracks.value = response.data.data
          total.value = response.data.meta.total
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const updateDifficultyRating = (track, rating) => {
      store.dispatch('tracks/updateTrack', { id: track.id, queryParams: { difficulty_rating: rating } })
        .then(() => {
          track.difficulty_rating = rating
        })
    }

    watch([page, sortBy, isSortDirDesc], () => {
      fetchPage()
    })

    fetchPage()

    return {
      tracks,
      isLoading,
      tableColumns,
      page,
      perPage,
      total,
      searchQuery,
      sortBy,
      isSortDirDesc,
      filterHasFile,
      filterIgnore,
      filterIsCover,
      filterHasCovers,
      filterPlayCountMin,
      filterPlayCountMax,
      filterDifficultyMin,
      filterDifficultyMax,
      boolOptions,
      fetchTracksList,
      updateDifficultyRating,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
